import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Divider,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import {
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Edit as EditIcon,
  ImageOutlined as ImageIcon,
  RecordVoiceOverOutlined as VoiceIcon,
  VideocamOutlined as VideoIcon,
  SpeedOutlined as SpeedIcon,
  AutoAwesomeOutlined as MultimodalIcon,
  DataArrayOutlined as EmbeddingIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { deploymentApi, usageApi } from "../services/omnimizer-api-service";
import { useTranslation } from "react-i18next";

const PredefinedDeploymentForm = ({ open, onClose, editModel }) => {
  const isEditMode = !!editModel;
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      namespace: "default",
      max_replicas: 1,
      model_source: "hf",
      model_url: "",
      args: {
        is_llm: true,
        deeplearning: null,
        llm: {
          is_embedding: false,
          is_multimodal: false,
          is_image: false,
          is_voice: false,
          is_video: false,
          speculative_model: false,
        },
      },
      resources: {
        cpu_request: "1",
        cpu_limit: "2",
        memory_request: "8Gi",
        memory_limit: "16Gi",
        gpu_count: 1,
        autopilot: true,
        gpu_type: "",
        storage: "18Gi",
      },
      credentials: "hf-system-token",
      is_active: true,
      model_metadata: {
        languages: [],
        best_for: [],
        supports: [],
        flags: [],
        model_size: "",
        provider: "",
      },
    },
  });

  const [loading, setLoading] = useState(false);
  const [languageInput, setLanguageInput] = useState("");
  const [bestForInput, setBestForInput] = useState("");
  const [supportsInput, setSupportsInput] = useState("");
  const [flagInput, setFlagInput] = useState("");
  const [gpuTypes, setGpuTypes] = useState([]);
  const [loadingGpuTypes, setLoadingGpuTypes] = useState(false);

  const modelSource = watch("model_source");
  const isLLM = watch("args.is_llm");
  const languages = watch("model_metadata.languages") || [];
  const bestFor = watch("model_metadata.best_for") || [];
  const supports = watch("model_metadata.supports") || [];
  const flags = watch("model_metadata.flags") || [];

  // Fetch GPU types when form opens
  useEffect(() => {
    if (open) {
      const fetchGpuTypes = async () => {
        setLoadingGpuTypes(true);
        try {
          const response = await usageApi.getGpuRates();
          if (
            response.data &&
            response.data.items &&
            Array.isArray(response.data.items)
          ) {
            const types = response.data.items.map((rate) => rate.gpu_type);
            setGpuTypes(types);

            // Set default GPU type if available and no type is set
            if (types.length > 0 && !watch("resources.gpu_type")) {
              setValue("resources.gpu_type", types[0]);
            }
          }
        } catch (error) {
          console.error("Error fetching GPU types:", error);
          toast.error(t("predefinedDeployment.failedToLoadGpuTypes"));
        } finally {
          setLoadingGpuTypes(false);
        }
      };

      fetchGpuTypes();
    }
  }, [open, setValue, watch]);

  // Set form values when editing
  useEffect(() => {
    if (editModel && open) {
      // Set all values from editModel
      reset({
        name: editModel.name || "",
        description: editModel.description || "",
        namespace: editModel.namespace || "default",
        max_replicas: editModel.max_replicas || 1,
        model_source: editModel.model_source || "hf",
        model_url: editModel.model_url || "",
        args: {
          is_llm: editModel.args?.is_llm ?? true,
          deeplearning: editModel.args?.deeplearning || null,
          llm: {
            is_embedding: editModel.args?.llm?.is_embedding ?? false,
            is_multimodal: editModel.args?.llm?.is_multimodal ?? false,
            is_image: editModel.args?.llm?.is_image ?? false,
            is_voice: editModel.args?.llm?.is_voice ?? false,
            is_video: editModel.args?.llm?.is_video ?? false,
            speculative_model: editModel.args?.llm?.speculative_model ?? false,
          },
        },
        resources: {
          cpu_request: editModel.resources?.cpu_request || "1",
          cpu_limit: editModel.resources?.cpu_limit || "2",
          memory_request: editModel.resources?.memory_request || "8Gi",
          memory_limit: editModel.resources?.memory_limit || "16Gi",
          gpu_count: editModel.resources?.gpu_count || 1,
          autopilot: editModel.resources?.autopilot ?? true,
          gpu_type: editModel.resources?.gpu_type || "",
          storage: editModel.resources?.storage || "18Gi",
        },
        credentials: editModel.credentials || "hf-system-token",
        is_active: editModel.is_active ?? true,
        model_metadata: {
          languages: editModel.model_metadata?.languages || [],
          best_for: editModel.model_metadata?.best_for || [],
          supports: editModel.model_metadata?.supports || [],
          flags: editModel.model_metadata?.flags || [],
          model_size: editModel.model_metadata?.model_size || "",
          provider: editModel.model_metadata?.provider || "",
        },
      });
    }
  }, [editModel, open, reset]);

  const handleAddLanguage = () => {
    if (!languageInput.trim()) return;
    const updatedLanguages = [...languages, languageInput.trim()];
    setValue("model_metadata.languages", updatedLanguages);
    setLanguageInput("");
  };

  const handleRemoveLanguage = (index) => {
    const updatedLanguages = [...languages];
    updatedLanguages.splice(index, 1);
    setValue("model_metadata.languages", updatedLanguages);
  };

  const handleAddBestFor = () => {
    if (!bestForInput.trim()) return;
    const updatedBestFor = [...bestFor, bestForInput.trim()];
    setValue("model_metadata.best_for", updatedBestFor);
    setBestForInput("");
  };

  const handleRemoveBestFor = (index) => {
    const updatedBestFor = [...bestFor];
    updatedBestFor.splice(index, 1);
    setValue("model_metadata.best_for", updatedBestFor);
  };

  const handleAddSupports = () => {
    if (!supportsInput.trim()) return;
    const updatedSupports = [...supports, supportsInput.trim()];
    setValue("model_metadata.supports", updatedSupports);
    setSupportsInput("");
  };

  const handleRemoveSupports = (index) => {
    const updatedSupports = [...supports];
    updatedSupports.splice(index, 1);
    setValue("model_metadata.supports", updatedSupports);
  };

  const handleAddFlag = () => {
    if (!flagInput.trim()) return;
    const updatedFlags = [...flags, flagInput.trim()];
    setValue("model_metadata.flags", updatedFlags);
    setFlagInput("");
  };

  const handleRemoveFlag = (index) => {
    const updatedFlags = [...flags];
    updatedFlags.splice(index, 1);
    setValue("model_metadata.flags", updatedFlags);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isEditMode) {
        await deploymentApi.updatePredefinedDeployment(editModel.id, data);
        toast.success(
          t("predefinedDeployment.updatedSuccess", { name: data.name }),
        );
      } else {
        await deploymentApi.createPredefinedDeployment(data);
        toast.success(t("predefinedDeployment.createdSuccess"));
      }
      reset();
      onClose();
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} predefined deployment:`,
        error,
      );
      toast.error(
        t(
          isEditMode
            ? "predefinedDeployment.failedToUpdate"
            : "predefinedDeployment.failedToCreate",
          { message: error.message },
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {isEditMode ? (
          <EditIcon color="primary" />
        ) : (
          <AdminIcon color="primary" />
        )}
        {isEditMode
          ? t("predefinedDeployment.editTitle")
          : t("predefinedDeployment.createTitle")}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t("modelRegistration.basicInformation")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                rules={{ required: t("predefinedDeployment.nameRequired") }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("predefinedDeployment.modelName")}
                    fullWidth
                    required
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="model_metadata.provider"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("common.provider")}
                    fullWidth
                    placeholder={t("predefinedDeployment.providerPlaceholder")}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                rules={{
                  required: t("predefinedDeployment.descriptionRequired"),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("common.description")}
                    fullWidth
                    multiline
                    rows={2}
                    required
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="namespace"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("predefinedDeployment.namespace")}
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="max_replicas"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("predefinedDeployment.maxReplicas")}
                    type="number"
                    fullWidth
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="model_source"
                control={control}
                rules={{
                  required: t("predefinedDeployment.modelSourceRequired"),
                }}
                render={({ field }) => (
                  <FormControl fullWidth required error={!!errors.model_source}>
                    <InputLabel>
                      {t("predefinedDeployment.modelSource")}
                    </InputLabel>
                    <Select
                      {...field}
                      label={t("predefinedDeployment.modelSource")}
                    >
                      <MenuItem value="hf">
                        {t("deployments.huggingFace")}
                      </MenuItem>
                      <MenuItem value="s3">
                        {t("deployments.s3Bucket")}
                      </MenuItem>
                      {/* SECURITY FIX: Commented out HTTP option to prevent SSRF vulnerability */}
                      {/* <MenuItem value="http">HTTP</MenuItem> */}
                      <MenuItem value="local">
                        {t("predefinedDeployment.local")}
                      </MenuItem>
                    </Select>
                    {errors.model_source && (
                      <FormHelperText>
                        {errors.model_source.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="model_url"
                control={control}
                rules={{ required: t("predefinedDeployment.modelUrlRequired") }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("predefinedDeployment.modelUrl")}
                    fullWidth
                    required
                    placeholder={
                      modelSource === "hf"
                        ? t("predefinedDeployment.huggingFaceModelPlaceholder")
                        : t("predefinedDeployment.fullUrlOrPathPlaceholder")
                    }
                    error={!!errors.model_url}
                    helperText={errors.model_url?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="model_metadata.model_size"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("predefinedDeployment.modelSize")}
                    fullWidth
                    placeholder={t("predefinedDeployment.modelSizePlaceholder")}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="credentials"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("predefinedDeployment.credentials")}
                    fullWidth
                    placeholder={t(
                      "predefinedDeployment.credentialsPlaceholder",
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="args.is_llm"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                        {...field}
                      />
                    }
                    label={t("predefinedDeployment.isLlmModel")}
                  />
                )}
              />
            </Grid>

            {isLLM && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    {t("deployments.llmCapabilities")}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={4} sm={3} md={2}>
                      <Controller
                        name="args.llm.is_embedding"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Card
                            sx={{
                              cursor: "pointer",
                              border: value ? "2px solid" : "1px solid",
                              borderColor: value ? "primary.main" : "divider",
                              height: "100%",
                              transition: "all 0.2s",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                              },
                            }}
                            onClick={() => onChange(!value)}
                          >
                            <CardContent
                              sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <EmbeddingIcon
                                  sx={{
                                    fontSize: 32,
                                    mb: 1,
                                    color: value
                                      ? "primary.main"
                                      : "text.secondary",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  align="center"
                                  fontWeight={500}
                                >
                                  {t("deployments.embedding")}
                                </Typography>
                                {value && (
                                  <Chip
                                    label={t("common.selected")}
                                    color="primary"
                                    size="small"
                                    sx={{
                                      mt: 1,
                                      height: 20,
                                      fontSize: "0.7rem",
                                    }}
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        )}
                      />
                    </Grid>

                    <Grid item xs={4} sm={3} md={2}>
                      <Controller
                        name="args.llm.is_multimodal"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Card
                            sx={{
                              cursor: "pointer",
                              border: value ? "2px solid" : "1px solid",
                              borderColor: value ? "primary.main" : "divider",
                              height: "100%",
                              transition: "all 0.2s",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                              },
                            }}
                            onClick={() => onChange(!value)}
                          >
                            <CardContent
                              sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <MultimodalIcon
                                  sx={{
                                    fontSize: 32,
                                    mb: 1,
                                    color: value
                                      ? "primary.main"
                                      : "text.secondary",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  align="center"
                                  fontWeight={500}
                                >
                                  {t("deployments.multimodal")}
                                </Typography>
                                {value && (
                                  <Chip
                                    label={t("common.selected")}
                                    color="primary"
                                    size="small"
                                    sx={{
                                      mt: 1,
                                      height: 20,
                                      fontSize: "0.7rem",
                                    }}
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        )}
                      />
                    </Grid>

                    <Grid item xs={4} sm={3} md={2}>
                      <Controller
                        name="args.llm.is_image"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Card
                            sx={{
                              cursor: "pointer",
                              border: value ? "2px solid" : "1px solid",
                              borderColor: value ? "primary.main" : "divider",
                              height: "100%",
                              transition: "all 0.2s",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                              },
                            }}
                            onClick={() => onChange(!value)}
                          >
                            <CardContent
                              sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <ImageIcon
                                  sx={{
                                    fontSize: 32,
                                    mb: 1,
                                    color: value
                                      ? "primary.main"
                                      : "text.secondary",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  align="center"
                                  fontWeight={500}
                                >
                                  {t("deployments.image")}
                                </Typography>
                                {value && (
                                  <Chip
                                    label={t("common.selected")}
                                    color="primary"
                                    size="small"
                                    sx={{
                                      mt: 1,
                                      height: 20,
                                      fontSize: "0.7rem",
                                    }}
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        )}
                      />
                    </Grid>

                    <Grid item xs={4} sm={3} md={2}>
                      <Controller
                        name="args.llm.is_voice"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Card
                            sx={{
                              cursor: "pointer",
                              border: value ? "2px solid" : "1px solid",
                              borderColor: value ? "primary.main" : "divider",
                              height: "100%",
                              transition: "all 0.2s",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                              },
                            }}
                            onClick={() => onChange(!value)}
                          >
                            <CardContent
                              sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <VoiceIcon
                                  sx={{
                                    fontSize: 32,
                                    mb: 1,
                                    color: value
                                      ? "primary.main"
                                      : "text.secondary",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  align="center"
                                  fontWeight={500}
                                >
                                  {t("deployments.audioVoice")}
                                </Typography>
                                {value && (
                                  <Chip
                                    label={t("common.selected")}
                                    color="primary"
                                    size="small"
                                    sx={{
                                      mt: 1,
                                      height: 20,
                                      fontSize: "0.7rem",
                                    }}
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        )}
                      />
                    </Grid>

                    <Grid item xs={4} sm={3} md={2}>
                      <Controller
                        name="args.llm.is_video"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Card
                            sx={{
                              cursor: "pointer",
                              border: value ? "2px solid" : "1px solid",
                              borderColor: value ? "primary.main" : "divider",
                              height: "100%",
                              transition: "all 0.2s",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                              },
                            }}
                            onClick={() => onChange(!value)}
                          >
                            <CardContent
                              sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <VideoIcon
                                  sx={{
                                    fontSize: 32,
                                    mb: 1,
                                    color: value
                                      ? "primary.main"
                                      : "text.secondary",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  align="center"
                                  fontWeight={500}
                                >
                                  {t("deployments.video")}
                                </Typography>
                                {value && (
                                  <Chip
                                    label={t("common.selected")}
                                    color="primary"
                                    size="small"
                                    sx={{
                                      mt: 1,
                                      height: 20,
                                      fontSize: "0.7rem",
                                    }}
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        )}
                      />
                    </Grid>

                    <Grid item xs={4} sm={3} md={2}>
                      <Controller
                        name="args.llm.speculative_model"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Card
                            sx={{
                              cursor: "pointer",
                              border: value ? "2px solid" : "1px solid",
                              borderColor: value ? "primary.main" : "divider",
                              height: "100%",
                              transition: "all 0.2s",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                              },
                            }}
                            onClick={() => onChange(!value)}
                          >
                            <CardContent
                              sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                }}
                              >
                                <SpeedIcon
                                  sx={{
                                    fontSize: 32,
                                    mb: 1,
                                    color: value
                                      ? "primary.main"
                                      : "text.secondary",
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  align="center"
                                  fontWeight={500}
                                >
                                  {t("deployments.speculativeDecoding")}
                                </Typography>
                                {value && (
                                  <Chip
                                    label={t("common.selected")}
                                    color="primary"
                                    size="small"
                                    sx={{
                                      mt: 1,
                                      height: 20,
                                      fontSize: "0.7rem",
                                    }}
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" gutterBottom>
                {t("predefinedDeployment.resourceConfiguration")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="resources.gpu_count"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("deployments.gpuCount")}
                    type="number"
                    fullWidth
                    InputProps={{ inputProps: { min: 1 } }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="resources.gpu_type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.resources?.gpu_type}>
                    <InputLabel>{t("predefinedModels.gpuType")}</InputLabel>
                    <Select
                      {...field}
                      label={t("predefinedModels.gpuType")}
                      disabled={loadingGpuTypes}
                      startAdornment={
                        loadingGpuTypes ? (
                          <InputAdornment position="start">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ) : null
                      }
                    >
                      {gpuTypes.length > 0 ? (
                        gpuTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled value="">
                          {loadingGpuTypes
                            ? t("predefinedDeployment.loadingGpuTypes")
                            : t("predefinedDeployment.noGpuTypesAvailable")}
                        </MenuItem>
                      )}
                    </Select>
                    {errors.resources?.gpu_type && (
                      <FormHelperText>
                        {errors.resources.gpu_type.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="resources.cpu_request"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("deploymentDetail.cpuRequest")}
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="resources.cpu_limit"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("deploymentDetail.cpuLimit")}
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="resources.memory_request"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("deploymentDetail.memoryRequest")}
                    fullWidth
                    placeholder={t("common.exampleValue", { value: "8Gi" })}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="resources.memory_limit"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("deploymentDetail.memoryLimit")}
                    fullWidth
                    placeholder={t("common.exampleValue", { value: "16Gi" })}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="resources.storage"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("predefinedModels.storage")}
                    fullWidth
                    placeholder={t("common.exampleValue", { value: "18Gi" })}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="resources.autopilot"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                        {...field}
                      />
                    }
                    label={t("predefinedDeployment.autopilotMode")}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="is_active"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                        {...field}
                      />
                    }
                    label={t("predefinedDeployment.activeVisibleToUsers")}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" gutterBottom>
                {t("predefinedDeployment.modelMetadata")}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t("predefinedDeployment.languages")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {languages.map((lang, index) => (
                  <Chip
                    key={index}
                    label={lang}
                    onDelete={() => handleRemoveLanguage(index)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  value={languageInput}
                  onChange={(e) => setLanguageInput(e.target.value)}
                  label={t("predefinedDeployment.addLanguage")}
                  size="small"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddLanguage();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleAddLanguage}
                          edge="end"
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t("predefinedDeployment.bestFor")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {bestFor.map((item, index) => (
                  <Chip
                    key={index}
                    label={item}
                    onDelete={() => handleRemoveBestFor(index)}
                    color="success"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  value={bestForInput}
                  onChange={(e) => setBestForInput(e.target.value)}
                  label={t("predefinedDeployment.addBestFor")}
                  size="small"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddBestFor();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleAddBestFor}
                          edge="end"
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t("predefinedDeployment.supports")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {supports.map((item, index) => (
                  <Chip
                    key={index}
                    label={item}
                    onDelete={() => handleRemoveSupports(index)}
                    color="info"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  value={supportsInput}
                  onChange={(e) => setSupportsInput(e.target.value)}
                  label={t("predefinedDeployment.addSupportType")}
                  size="small"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSupports();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleAddSupports}
                          edge="end"
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t("predefinedDeployment.flags")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {flags.map((item, index) => (
                  <Chip
                    key={index}
                    label={item}
                    onDelete={() => handleRemoveFlag(index)}
                    color="warning"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  value={flagInput}
                  onChange={(e) => setFlagInput(e.target.value)}
                  label={t("predefinedDeployment.addFlag")}
                  size="small"
                  placeholder={t("predefinedDeployment.flagPlaceholder")}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddFlag();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleAddFlag}
                          edge="end"
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary" variant="text">
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={20} />
              ) : isEditMode ? (
                <EditIcon />
              ) : (
                <AdminIcon />
              )
            }
          >
            {loading
              ? isEditMode
                ? t("common.updating")
                : t("common.creating")
              : isEditMode
                ? t("common.edit")
                : t("common.create")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PredefinedDeploymentForm;
